import { Fragment, type ReactNode } from "react";
import { Lexer, type Token, type Tokens } from "marked";
import { cx } from "../cx";

/**
 * El cuerpo markdown de una ficha de la biblioteca, en la prosa del sistema
 * (ficha.html): cada `## Sección` es un `<section>` con su título en
 * overline (sin el paréntesis de la plantilla: «Qué es (cinco líneas)» →
 * «Qué es»), párrafos, viñetas, tablas simples, las citas `[Fn]` en mono y
 * cada `[CRITERIO PENDIENTE: …]` donde la pantalla diga (`marcador`).
 *
 * SANEADO POR CONSTRUCCIÓN: `marked` solo TOKENIZA; aquí se pinta cada token
 * como elemento de React, nunca con `innerHTML`. El HTML crudo que traiga
 * el markdown sale como texto, y un enlace solo es enlace si va a `http(s)`
 * o `mailto` (cualquier otro esquema, `javascript:` incluido, queda en
 * texto). Las fichas son contenido del repo, pero se sanea igual.
 */
export interface FichaProseProps {
  markdown: string;
  /** Slugs de otras fichas que se pueden enlazar (los `related_items`). */
  slugs?: ReadonlySet<string> | undefined;
  /** Cómo pintar un slug enlazable (un Link del router); por defecto, texto. */
  renderSlug?: ((slug: string) => ReactNode) | undefined;
  /** Qué pintar en el sitio de cada `[CRITERIO PENDIENTE]`, en el orden del cuerpo. */
  marcador?: ((i: number, pregunta: string, enLinea: boolean) => ReactNode) | undefined;
  /** Sin el `# Título` (la cabecera de la página ya lo lleva). */
  sinTitulo?: boolean | undefined;
  /** Solo los bloques, sin `<article>` ni secciones (un trozo: la vista previa de una firma). */
  bare?: boolean | undefined;
  className?: string | undefined;
}

/** El marcador del método (el mismo patrón que `check-fichas` y la API). */
export const CRITERIO_PENDIENTE = /\[CRITERIO\s+PENDIENTE:\s*([\s\S]*?)\]/g;
const CITA = /\[F\d+\]/g;
const SLUG = /\b[a-z0-9]+(?:-[a-z0-9]+)+\b|\b[a-z0-9]+(?= \(C\d\d\))|(?<=\bver )[a-z0-9]+\b/g;
const pregunta = (s: string) => s.replace(/\s+/g, " ").trim();
const seguro = (href: string) => /^(https?:|mailto:)/i.test(href.trim());
const tituloSeccion = (t: string) => t.replace(/\s*\([^)]*\)\s*$/, "").trim();

interface Ctx { n: number; props: FichaProseProps }

function marca(ctx: Ctx, texto: string, enLinea: boolean, key: string): ReactNode {
  const i = ctx.n++;
  const q = pregunta(texto);
  if (ctx.props.marcador) return <Fragment key={key}>{ctx.props.marcador(i, q, enLinea)}</Fragment>;
  return enLinea
    ? <span key={key} className="sk-whisper">[pregunta pendiente: {q}]</span>
    : <p key={key} className="sk-whisper">Pregunta pendiente: {q}</p>;
}

/** Texto plano → citas en mono, slugs enlazables y marcadores. */
function texto(ctx: Ctx, s: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  const flush = (to: number) => { if (to > last) out.push(...citasYSlugs(ctx, s.slice(last, to), `${key}-${k++}`)); };
  for (const m of s.matchAll(CRITERIO_PENDIENTE)) {
    flush(m.index);
    out.push(marca(ctx, m[1] ?? "", true, `${key}-m${k++}`));
    last = m.index + m[0].length;
  }
  flush(s.length);
  return out;
}
function citasYSlugs(ctx: Ctx, s: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  const { slugs, renderSlug } = ctx.props;
  const re = new RegExp(`${CITA.source}|${SLUG.source}`, "g");
  for (const m of s.matchAll(re)) {
    const w = m[0];
    const cita = /^\[F\d+\]$/.test(w);
    if (!cita && !(slugs?.has(w) === true && renderSlug)) continue;
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(cita ? <span key={`${key}-${k++}`} className="sk-mono">{w}</span> : <Fragment key={`${key}-${k++}`}>{renderSlug?.(w)}</Fragment>);
    last = m.index + w.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

function inline(ctx: Ctx, tokens: Token[] | undefined, key: string): ReactNode[] {
  return (tokens ?? []).map((t, i): ReactNode => {
    const k = `${key}.${i}`;
    switch (t.type) {
      case "strong": return <b key={k}>{inline(ctx, (t as Tokens.Strong).tokens, k)}</b>;
      case "em": return <i key={k}>{inline(ctx, (t as Tokens.Em).tokens, k)}</i>;
      case "del": return <s key={k}>{inline(ctx, (t as Tokens.Del).tokens, k)}</s>;
      case "codespan": return <span key={k} className="sk-mono">{(t as Tokens.Codespan).text}</span>;
      case "br": return <br key={k} />;
      case "link": {
        const l = t as Tokens.Link;
        const body = inline(ctx, l.tokens, k);
        return seguro(l.href) ? <a key={k} href={l.href} target="_blank" rel="noopener noreferrer">{body}</a> : <Fragment key={k}>{body}</Fragment>;
      }
      case "text": {
        const x = t as Tokens.Text;
        return <Fragment key={k}>{x.tokens ? inline(ctx, x.tokens, k) : texto(ctx, x.text, k)}</Fragment>;
      }
      // HTML crudo, escapes, imágenes y lo que no se conoce: su texto, nunca su marcado
      default: return <Fragment key={k}>{texto(ctx, "text" in t && typeof t.text === "string" ? t.text : t.raw, k)}</Fragment>;
    }
  });
}

function block(ctx: Ctx, t: Token, key: string): ReactNode {
  switch (t.type) {
    case "space":
    case "hr": return null;
    case "paragraph": {
      const p = t as Tokens.Paragraph;
      const solo = p.text.trim().match(/^\[CRITERIO\s+PENDIENTE:\s*([\s\S]*?)\]$/);
      if (solo) return marca(ctx, solo[1] ?? "", false, key);
      return <p key={key}>{inline(ctx, p.tokens, key)}</p>;
    }
    case "text": {
      const x = t as Tokens.Text;
      return <Fragment key={key}>{x.tokens ? inline(ctx, x.tokens, key) : texto(ctx, x.text, key)}</Fragment>;
    }
    case "list": {
      const l = t as Tokens.List;
      // las viñetas de una ficha son tesis con su arranque en negrita: el
      // contrato (ficha.html) las lee como párrafos de prosa, uno por viñeta
      if (!l.ordered) {
        return (
          <Fragment key={key}>
            {l.items.map((it, i) => {
              const hijos = it.tokens.map((x, j) => block(ctx, x, `${key}.${i}.${j}`));
              // lista «suelta»: sus hijos ya son párrafos
              return it.tokens.some((x) => x.type !== "text" && x.type !== "space") ? <Fragment key={i}>{hijos}</Fragment> : <p key={i}>{hijos}</p>;
            })}
          </Fragment>
        );
      }
      return <ol key={key}>{l.items.map((it, i) => <li key={i}>{it.tokens.map((x, j) => block(ctx, x, `${key}.${i}.${j}`))}</li>)}</ol>;
    }
    case "table": {
      const tb = t as Tokens.Table;
      return (
        <div key={key} className="sk-tablewrap">
          <table className="sk-table">
            <thead><tr>{tb.header.map((c, i) => <th key={i}>{inline(ctx, c.tokens, `${key}.h${i}`)}</th>)}</tr></thead>
            <tbody>{tb.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{inline(ctx, c.tokens, `${key}.${i}.${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
    }
    case "blockquote": return <blockquote key={key}>{(t as Tokens.Blockquote).tokens.map((x, i) => block(ctx, x, `${key}.${i}`))}</blockquote>;
    case "code": return <pre key={key} className="sk-mono" style={{ overflowX: "auto" }}>{(t as Tokens.Code).text}</pre>;
    case "heading": {
      const h = t as Tokens.Heading;
      return <p key={key}><b>{inline(ctx, h.tokens, key)}</b></p>;
    }
    default: return <p key={key}>{texto(ctx, "text" in t && typeof t.text === "string" ? t.text : t.raw, key)}</p>;
  }
}

export function FichaProse(props: FichaProseProps) {
  const tokens = new Lexer({ gfm: true }).lex(props.markdown);
  const ctx: Ctx = { n: 0, props };
  if (props.bare === true) return <>{tokens.map((t, i) => block(ctx, t, `b${i}`))}</>;

  // secciones: cada `## ` abre una; lo que va antes (sin el `# Título`), una sin cabecera
  const secciones: { titulo: string | null; tokens: Token[] }[] = [{ titulo: null, tokens: [] }];
  for (const t of tokens) {
    if (t.type === "heading" && (t as Tokens.Heading).depth === 1 && props.sinTitulo === true) continue;
    if (t.type === "heading" && (t as Tokens.Heading).depth === 2) { secciones.push({ titulo: tituloSeccion((t as Tokens.Heading).text), tokens: [] }); continue; }
    secciones[secciones.length - 1]?.tokens.push(t);
  }
  return (
    <article className={cx("sk-prose", props.className)}>
      {secciones.filter((s) => s.titulo !== null || s.tokens.some((t) => t.type !== "space")).map((s, i) => (
        <section key={i}>
          {s.titulo !== null && <h2 className="sk-overline">{s.titulo}</h2>}
          <div>{s.tokens.map((t, j) => block(ctx, t, `s${i}.${j}`))}</div>
        </section>
      ))}
    </article>
  );
}
